import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { UserService } from '~/app/services/user/user.service';
import type { CreateOrganizationUserRequestDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.request.dto';
import type { ListOrganizationUsersDto } from '@evimo/types/api/user/list-organization-users/list-organization-users.dto';
import type { UpdateUserRequestDto } from '@evimo/types/api/user/update-user/update-user.request.dto';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { Alert, Card, Space, Tabs, Typography } from 'antd';
import { UserManagementTable } from './components/UserManagementTable';
import { OrgUserRole, parseOrgUserRole } from './utils';

const { Paragraph, Title } = Typography;

export function PeoplePageContainer() {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation('organization');
  const [activeTab, setActiveTab] = useState<OrgUserRole>(
    OrgUserRole.SUPERVISOR,
  );
  const [users, setUsers] = useState<ListOrganizationUsersDto[]>([]);
  const [loading, setLoading] = useState(false);

  const organizationId = currentUser?.organizationId;

  const loadUsersByRole = async (role: OrgUserRole) => {
    if (!organizationId) return;
    setLoading(true);
    try {
      // Organization managers can see users from sub-orgs too
      const includeSuborgs =
        currentUser?.roleName === SystemRoleName.ORGANIZATION_MANAGER;
      const data = await UserService.listOrganizationUsers(
        organizationId,
        role,
        includeSuborgs,
      );
      setUsers(data);
    } catch (error) {
      console.error(`Failed to load ${role}s:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsersByRole(activeTab);
  }, [organizationId, activeTab]);

  const handleCreateUser = async (data: CreateOrganizationUserRequestDto) => {
    if (!organizationId) return;
    try {
      await UserService.createOrganizationUser(organizationId, {
        ...data,
        organizationId,
        role: activeTab,
      });
      await loadUsersByRole(activeTab);
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (
    id: string,
    data: Partial<UpdateUserRequestDto>,
  ) => {
    if (!organizationId) return;
    try {
      await UserService.updateUser(organizationId, id, data);
      await loadUsersByRole(activeTab);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!organizationId) return;
    try {
      await UserService.deleteUser(organizationId, id);
      await loadUsersByRole(activeTab);
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  };

  const getPermissions = (targetUser: ListOrganizationUsersDto) => {
    if (!currentUser) return { canEdit: false, canDelete: false };

    const isSelf = currentUser.id === targetUser.id;

    if (currentUser.roleName === SystemRoleName.SUPER_ADMIN) {
      return { canEdit: true, canDelete: true };
    }

    if (currentUser.roleName === SystemRoleName.ORGANIZATION_MANAGER) {
      // Can edit supervisors and technicians and himself
      if (isSelf) return { canEdit: true, canDelete: false };
      if (
        targetUser.roleName === SystemRoleName.SUPERVISOR ||
        targetUser.roleName === SystemRoleName.FIELD_TECHNICIAN
      ) {
        return { canEdit: true, canDelete: true };
      }
      return { canEdit: false, canDelete: false };
    }

    if (currentUser.roleName === SystemRoleName.SUPERVISOR) {
      // Can edit technicians and himself
      if (isSelf) return { canEdit: true, canDelete: false };
      if (targetUser.roleName === SystemRoleName.FIELD_TECHNICIAN) {
        return { canEdit: true, canDelete: true };
      }
      return { canEdit: false, canDelete: false };
    }

    return { canEdit: false, canDelete: false };
  };

  const canCreate = () => {
    if (!currentUser) return false;
    if (currentUser.roleName === SystemRoleName.SUPER_ADMIN) return true;

    if (currentUser.roleName === SystemRoleName.ORGANIZATION_MANAGER) {
      return (
        activeTab === OrgUserRole.SUPERVISOR ||
        activeTab === OrgUserRole.FIELD_TECHNICIAN
      );
    }

    if (currentUser.roleName === SystemRoleName.SUPERVISOR) {
      return activeTab === OrgUserRole.FIELD_TECHNICIAN;
    }

    return false;
  };

  if (!organizationId) {
    return (
      <Card style={{ margin: '24px' }}>
        <Alert
          message={t(
            'people.noOrganization',
            'No organization associated with your account',
          )}
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  const items = [
    {
      key: OrgUserRole.ORGANIZATION_MANAGER,
      label: t('people.tabs.managers', 'Managers'),
    },
    {
      key: OrgUserRole.SUPERVISOR,
      label: t('people.tabs.supervisors', 'Supervisors'),
    },
    {
      key: OrgUserRole.FIELD_TECHNICIAN,
      label: t('people.tabs.technicians', 'Technicians'),
    },
  ].filter((item) => {
    if (currentUser?.roleName === SystemRoleName.SUPER_ADMIN) return true;
    if (currentUser?.roleName === SystemRoleName.ORGANIZATION_MANAGER)
      return true;
    // Supervisors can only see supervisors and technicians
    return item.key !== OrgUserRole.ORGANIZATION_MANAGER;
  });

  return (
    <div className="app-page">
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            {t('people.page.title', 'Users')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t(
              'people.page.description',
              'Manage managers, supervisors, and field technicians for your organization workspace.',
            )}
          </Paragraph>
        </div>

        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(parseOrgUserRole(key))}
            items={items}
          />

          <UserManagementTable
            role={activeTab}
            users={users}
            loading={loading}
            onRefresh={() => loadUsersByRole(activeTab)}
            onCreate={handleCreateUser}
            onUpdate={handleUpdateUser}
            onDelete={handleDeleteUser}
            permissions={getPermissions}
            canCreate={canCreate()}
          />
        </Card>
      </Space>
    </div>
  );
}
